'use client';

import { useState, useMemo, useCallback, memo, useRef, useLayoutEffect } from 'react';

import { ResourceDownloadIcon } from '../Icons/Icons';
import { sanitizeRteContent } from '@/lib/sanitize-rte-content';
import { useCodeHighlight } from '@/hooks/useCodeHighlight';
import FeedbackTab from './FeedbackTab';
import LearningPathTabCard from './LearningPathTabCard';
import MaterialsTabComponent from './MaterialsTab';
import { useUserLearningPaths } from '@/hooks/useUserLearningPaths';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { hasPartnerTag } from '@/lib/constants/partner';

type TabId =
  | 'overview'
  | 'transcript'
  | 'text_lesson'
  | 'materials'
  | 'learning_paths'
  | 'feedback';

interface FileObject {
  url?: string;
  title?: string;
  filename?: string;
  [key: string]: any;
}

interface MaterialItem {
  id?: string;
  type?:
    | 'Download Related Files'
    | 'Code Samples'
    | 'Exercise Files'
    | 'Custom CTA'
    | 'View FAQ'
    | string
    | null;
  file?: FileObject;
  url?: string;
  custom_icon?: FileObject;
  custom_type?: string;
}

interface OverviewContent {
  description?: string;
  overview?: string;
}

interface TabsAndNavsProps {
  overview?: OverviewContent | null;
  transcript?: string | null;
  materials?: MaterialItem[] | null;
  textLesson?: string | null;
  learning_paths?: unknown[] | null;
  showFeedback?: boolean;
  feedbackEntryUid?: string;
  feedbackTitle?: string;
  feedbackType?: 'course' | 'lesson' | 'learning-path';
  defaultTab?: TabId;
  showOnlyProvidedTabs?: boolean;
}

/** Wraps iframes/videos in .video-wrapper (for rte-content.css 16:9) and adds video controls. */
function wrapMediaInContainer(container: HTMLElement) {
  const wrap = (el: Element) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'video-wrapper';
    el.parentNode?.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  };

  for (const el of container.querySelectorAll<HTMLElement>('iframe, video')) {
    if (el.closest('.video-wrapper')) continue;
    wrap(el);
    if (el.tagName === 'VIDEO' && !el.hasAttribute('controls')) el.setAttribute('controls', '');
  }
}

/** Renders HTML in a div and wraps iframes/videos in .video-wrapper after each commit. */
const RteContentWithMedia = memo(({ html, className }: { html: string; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ref.current) wrapMediaInContainer(ref.current);
  });
  useCodeHighlight(ref);
  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: html }} />;
});
RteContentWithMedia.displayName = 'RteContentWithMedia';

const OverviewTab = memo(({ content }: { content?: OverviewContent | null }) => {
  const rawHtml =
    content?.description || content?.overview
      ? `${content?.description ?? ''}${content?.overview ?? ''}`
      : '<No content available>';
  const html = sanitizeRteContent(rawHtml);
  const hasContent = !!(content?.description || content?.overview);
  return (
    <RteContentWithMedia
      html={html}
      className={hasContent ? 'rte-content cs-m-body' : 'cs-m-body'}
    />
  );
});
OverviewTab.displayName = 'OverviewTab';

const TranscriptTab = memo(({ content }: { content?: string | null }) => {
  if (!content) return null;
  return <RteContentWithMedia html={content} className="rte-content cs-m-body" />;
});
TranscriptTab.displayName = 'TranscriptTab';

const TextLessonTab = memo(({ content }: { content?: string | null }) => {
  const html =
    content?.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
      if (/target\s*=/i.test(attributes)) return match;
      return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
    }) ?? '';
  if (!content) return null;
  return <RteContentWithMedia html={html} className="rte-content cs-m-body" />;
});
TextLessonTab.displayName = 'TextLessonTab';

const MaterialsTab = memo(({ content }: { content?: MaterialItem[] | null }) => {
  return (
    <div className="w-full">
      <MaterialsTabComponent ctaData={content || []} />
    </div>
  );
});
MaterialsTab.displayName = 'MaterialsTab';

interface LearningPath {
  uid: string;
  url: string;
  title?: string;
  is_assessment?: boolean;
  is_partner_specific?: boolean;
  learning_pathway_details?: {
    thumbnail?: { url: string };
    headline?: string;
    description?: string;
    awards_certifications?: boolean;
    videos_count?: number;
  };
  courses?: {
    course?: unknown[];
  };
}

const LearningPathsTab = memo(({ content }: { content?: unknown[] | null }) => {
  const learningPaths = content as LearningPath[] | null;
  const { enrolledMap } = useUserLearningPaths();
  const { user } = useAuthUser();
  const isPartner = !!user && hasPartnerTag(user);

  if (!learningPaths?.length) {
    return (
      <div className="py-8 text-center">
        <p className="text-white/60">No learning paths referenced for this course.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {learningPaths.map((path) => {
        const { isSubscribed = false, hasStarted = false, progressPercent, isCompleted, isLearningMaterialCompleted } =
          enrolledMap.get(path.uid) || {};

        // Partners see all paths (partner-specific + non-partner); non-partners see only non-partner paths
        const showCard = isPartner || path.is_partner_specific !== true;
        if (!showCard) return null;

        return (
          <LearningPathTabCard
            key={path.uid}
            uid={path.uid}
            url={`/learning-paths${path.url}`}
            thumbnail={path?.learning_pathway_details?.thumbnail?.url}
            title={path?.learning_pathway_details?.headline || path?.title || ''}
            description={path?.learning_pathway_details?.description || ''}
            isEnrolled={isSubscribed}
            hasStarted={hasStarted}
            progressPercent={progressPercent}
            isCompleted={isCompleted}
            isLearningMaterialCompleted={isLearningMaterialCompleted}
            hasAssessment={path?.is_assessment === true}
          />
        );
      })}
    </div>
  );
});
LearningPathsTab.displayName = 'LearningPathsTab';

export default function TabsAndNavs({
  overview,
  transcript,
  materials,
  learning_paths,
  textLesson,
  showFeedback = false,
  feedbackEntryUid,
  feedbackTitle,
  feedbackType,
  defaultTab,
  showOnlyProvidedTabs = false,
}: TabsAndNavsProps) {
  // Memoize tab configuration to prevent recreation on every render
  const tabs = useMemo(() => {
    const allTabs = [
      {
        id: 'overview' as const,
        label: 'Overview',
        hasContent: !!(overview?.description || overview?.overview),
      },
      { id: 'transcript' as const, label: 'Transcript', hasContent: !!transcript },
      {
        id: 'text_lesson' as const,
        label: 'Text Lesson',
        hasContent: !!textLesson,
      },
      {
        id: 'materials' as const,
        label: 'Materials',
        hasContent: !!materials && Array.isArray(materials) && materials.length > 0,
      },
      {
        id: 'learning_paths' as const,
        label: 'Learning Paths',
        hasContent: !!learning_paths && Array.isArray(learning_paths) && learning_paths.length > 0,
      },
      {
        id: 'feedback' as const,
        label: 'Feedback',
        hasContent: showFeedback,
      },
    ];

    return showOnlyProvidedTabs ? allTabs.filter((tab) => tab.hasContent) : allTabs;
  }, [
    overview,
    transcript,
    materials,
    learning_paths,
    textLesson,
    showFeedback,
    showOnlyProvidedTabs,
  ]);

  // Determine initial active tab with validation
  const initialTab = useMemo(() => {
    // If defaultTab is provided and exists in available tabs, use it
    if (defaultTab && tabs.some((tab) => tab.id === defaultTab)) {
      return defaultTab;
    }
    // Otherwise, fall back to first available tab
    return tabs[0]?.id || 'overview';
  }, [defaultTab, tabs]);

  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  // Memoize click handler to prevent recreation on every render
  const handleTabClick = useCallback((tabId: TabId) => {
    setActiveTab(tabId);
  }, []);

  // Lazy render: Only render the active tab component
  const activeTabContent = useMemo(() => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab content={overview} />;
      case 'transcript':
        return <TranscriptTab content={transcript} />;
      case 'materials':
        return <MaterialsTab content={materials} />;
      case 'text_lesson':
        return <TextLessonTab content={textLesson} />;
      case 'learning_paths':
        return <LearningPathsTab content={learning_paths} />;
      case 'feedback':
        return feedbackEntryUid && feedbackTitle && feedbackType ? (
          <FeedbackTab entryUid={feedbackEntryUid} title={feedbackTitle} type={feedbackType} />
        ) : null;
      default:
        return null;
    }
  }, [
    activeTab,
    overview,
    transcript,
    materials,
    learning_paths,
    textLesson,
    feedbackEntryUid,
    feedbackTitle,
    feedbackType,
  ]);

  // Handle case where no tabs are available
  if (tabs.length === 0) {
    return <div className="w-full p-8 text-center">No content available</div>;
  }

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="bg-surface-2 scrollbar-hide h-[auto] overflow-hidden overflow-x-auto px-2 py-3 lg:px-8">
        <nav className="flex flex-nowrap gap-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`cs-s-body-regular relative flex-shrink-0 px-4 py-3 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-[var(--Text-Default-Strong)]'
                  : 'text-[var(--Text-Default-Light)]'
              } `}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              {tab.label}

              {/* Active indicator */}
              {activeTab === tab.id && (
                <span className="absolute right-0 bottom-[-12] left-0 h-0.5 bg-[var(--Color-Extended-Amethyst-Amethyst-Accent)]" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content - Lazy rendered */}
      <div className="bg-surface-1 py-8 px-8">{activeTabContent}</div>
    </div>
  );
}
